import { Schema, NodeSpec, MarkSpec, DOMOutputSpec, Node } from 'prosemirror-model'
import { addListNodes } from 'prosemirror-schema-list'
import OrderedMap from 'orderedmap'

/*
  copied from prosemirror-example-basic
  @see https://github.com/ProseMirror/prosemirror-schema-basic/blob/master/src/schema-basic.ts
 */

const pDOM: DOMOutputSpec = ['p', 0]
const brDOM: DOMOutputSpec = ['br']

const nodes = {
  doc: {
    content: 'block+',
  } as NodeSpec,

  paragraph: {
    content: 'inline*',
    group: 'block',
    parseDOM: [{ tag: 'p' }],
    toDOM() {
      return pDOM
    },
  } as NodeSpec,

  text: {
    group: 'inline',
  } as NodeSpec,

  hard_break: {
    inline: true,
    group: 'inline',
    selectable: false,
    parseDOM: [{ tag: 'br' }],
    toDOM() {
      return brDOM
    },
  } as NodeSpec,
  pill: {
    inline: true,
    group: 'inline',
    attrs: {
      text: { default: '' }, // String to display
    },
    atom: true,
    selectable: true,
    parseDOM: [
      {
        tag: 'pill',
        getAttrs: (dom) => {
          const domElement = dom as Element
          return {
            text: domElement.getAttribute('data-text'),
          }
        },
      },
    ],
    toDOM: (node) => {
      const {
        attrs: { text },
      } = node
      return ['pill', { 'data-text': text }, text]
    },
  } as NodeSpec,
  smart_link: {
    inline: true,
    group: 'inline',
    attrs: {
      type: { default: 'Article' }, // 'Article' | 'User'
      key: { default: '' }, // Article key when type === 'Article'
      userId: { default: '' }, // User id when type === 'User'
      title: { default: '' }, // String to display
      url: { default: '' }, // Url to link to
    },
    atom: true,
    selectable: true,
    parseDOM: [
      {
        tag: 'smart-link',
        getAttrs: (dom) => {
          const domElement = dom as Element
          return {
            type: domElement.getAttribute('data-type'),
            key: domElement.getAttribute('data-key'),
            userId: domElement.getAttribute('data-user-id'),
            title: domElement.getAttribute('data-title'),
            url: domElement.getAttribute('data-url'),
          }
        },
      },
    ],
    toDOM: (node) => {
      const {
        attrs: { type, key, userId, title, url },
      } = node
      return [
        'smart-link',
        { 'data-type': type, 'data-key': key, 'data-user-id': userId, 'data-title': title, 'data-url': url },
        `${type === 'User' ? '@' : ''}${title}`,
      ]
    },
  } as NodeSpec,
}

const emDOM: DOMOutputSpec = ['em', 0]
const strongDOM: DOMOutputSpec = ['strong', 0]
const codeDOM: DOMOutputSpec = ['code', 0]
const linkRel = 'nofollow noopener noreferrer'

// TODO: Find a way to import HTMLAttributeAnchorTarget
function targetFromHref(href: string | null) {
  if (href != null && /^#/.test(href)) return '_self'
  return '_blank'
}

const marks = {
  link: {
    attrs: {
      href: {},
      title: { default: null },
      target: { default: '_blank' },
      rel: { default: linkRel },
    },
    inclusive: false,
    parseDOM: [
      {
        tag: 'a[href]',
        getAttrs(dom: HTMLElement) {
          const href = dom.getAttribute('href')
          return { href, title: dom.getAttribute('title'), target: targetFromHref(href), rel: linkRel }
        },
      },
    ],
    toDOM(node: Node) {
      const { href, title } = node.attrs
      return ['a', { href, title, target: targetFromHref(href), rel: linkRel }, 0]
    },
  } as unknown as MarkSpec,

  em: {
    parseDOM: [{ tag: 'i' }, { tag: 'em' }, { style: 'font-style=italic' }],
    toDOM() {
      return emDOM
    },
  } as MarkSpec,

  strong: {
    parseDOM: [
      { tag: 'strong' },
      { tag: 'b', getAttrs: (node: HTMLElement) => node.style.fontWeight !== 'normal' && null },
      { style: 'font-weight', getAttrs: (value: string) => /^(bold(er)?|[5-9]\d{2,})$/.test(value) && null },
    ],
    toDOM() {
      return strongDOM
    },
  } as MarkSpec,

  code: {
    parseDOM: [{ tag: 'code' }],
    toDOM() {
      return codeDOM
    },
    excludes: 'em strong underline',
  } as MarkSpec,

  underline: {
    parseDOM: [{ tag: 'u' }],
    toDOM: () => ['u', 0],
  } as MarkSpec,
}

const basicSchema = new Schema({ nodes, marks })
export const schema = new Schema({
  nodes: addListNodes(basicSchema.spec.nodes as OrderedMap<NodeSpec>, 'paragraph block*', 'block'),
  marks: basicSchema.spec.marks,
})

export const textSchema = new Schema({
  nodes: {
    text: {
      group: 'inline',
    },
    paragraph: {
      group: 'block',
      content: 'inline*',
      toDOM() {
        return ['p', 0]
      },
      parseDOM: [{ tag: 'p' }],
    },
    doc: {
      content: 'block+',
    },
  },
})
